// Shared rolling-hill terrain height field. Evaluated identically by the
// server (sets each tank's cosmetic Y so it "sits" on the ground) and the
// client (rendering + local prediction) — a pure function of (x, z), no
// physics engine involved, so it can never desync the authoritative XZ
// simulation (see the design note in ArenaBuilder.ts / classes.ts on why
// elevation stays OUT of Rapier). Deliberately simple (a couple of sine
// octaves) rather than a real noise library: cheap enough to call every tick
// on both sides, and easy to reason about/tune.
import { ARENA, SPAWN_POINTS, POWERUP_POINTS, OBSTACLES } from "./constants.js";
/** Two octaves: broad rolling hills + a finer ripple on top. Amplitude tuned
 * for "moderate rolling" — noticeable, never blocks a tank's climb. */
const OCTAVES = [
    { wx: 1 / 32, wz: 1 / 27, amp: 1.5, px: 1.7, pz: 0.6 },
    { wx: 1 / 15, wz: 1 / 19, amp: 0.55, px: 4.1, pz: 2.9 },
];
function rawHeight(x, z) {
    let h = 0;
    for (const o of OCTAVES) {
        h += Math.sin(x * o.wx * Math.PI * 2 + o.px) * Math.cos(z * o.wz * Math.PI * 2 + o.pz) * o.amp;
    }
    return h;
}
// Anything gameplay-critical (spawns, power-ups, obstacle footprints — future
// building footprints will join this list in the buildings pass) sits on a
// perfectly flat pad, blended smoothly back into the rolling terrain so nothing
// pops. Obstacle pad radius = the exact half-diagonal of its w/d footprint
// (so hills never poke through a wall's collider) plus a small margin.
const PADS = [
    ...SPAWN_POINTS.map((p) => ({ x: p.x, z: p.z, r: 6 })),
    ...POWERUP_POINTS.map((p) => ({ x: p.x, z: p.z, r: 4 })),
    ...OBSTACLES.map((o) => ({ x: o.x, z: o.z, r: Math.hypot(o.w, o.d) / 2 + 2 })),
];
// Blend distance at the edge of each pad (wide relative to the terrain's own
// wavelength, see OCTAVES above) — this keeps the SYNTHETIC slope created by
// the blend itself (going from flat to full terrain height) no steeper than
// the rolling terrain already is. A narrow blend was tried and rejected: a
// tank sitting right at a pad's edge tilted hard for a couple of meters,
// reading as a broken render rather than a hill (found during an actual
// in-browser playtest).
const PAD_BLEND_M = 9;
const WALL_BAND_M = 6; // meters of flattening along the arena perimeter (wall footing)
/** Smoothstep (3t²-2t³): zero derivative at both t=0 and t=1, so the blend
 * ramp itself never introduces a slope discontinuity at the pad boundary. */
function smoothstep(t) {
    const c = Math.max(0, Math.min(1, t));
    return c * c * (3 - 2 * c);
}
/** 0 = fully flattened, 1 = full terrain height. Smallest (flattest) value wins. */
function flattenFactor(x, z) {
    let factor = 1;
    for (const pad of PADS) {
        const d = Math.hypot(x - pad.x, z - pad.z);
        if (d <= pad.r)
            return 0;
        if (d < pad.r + PAD_BLEND_M) {
            factor = Math.min(factor, smoothstep((d - pad.r) / PAD_BLEND_M));
        }
    }
    const edgeDist = ARENA.HALF - Math.max(Math.abs(x), Math.abs(z));
    if (edgeDist < WALL_BAND_M) {
        factor = Math.min(factor, smoothstep(Math.max(0, edgeDist / WALL_BAND_M)));
    }
    return factor;
}
/** Terrain height (world Y, meters) at an arena XZ position. Pure + deterministic. */
export function terrainHeight(x, z) {
    return rawHeight(x, z) * flattenFactor(x, z);
}
const NORMAL_EPS = 0.5;
/** Surface normal at an arena XZ position (finite-difference of terrainHeight). */
export function terrainNormal(x, z) {
    const hL = terrainHeight(x - NORMAL_EPS, z);
    const hR = terrainHeight(x + NORMAL_EPS, z);
    const hD = terrainHeight(x, z - NORMAL_EPS);
    const hU = terrainHeight(x, z + NORMAL_EPS);
    const dx = (hR - hL) / (2 * NORMAL_EPS);
    const dz = (hU - hD) / (2 * NORMAL_EPS);
    const nx = -dx;
    const ny = 1;
    const nz = -dz;
    const len = Math.hypot(nx, ny, nz) || 1;
    return { x: nx / len, y: ny / len, z: nz / len };
}
//# sourceMappingURL=terrain.js.map